import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Dropdown,
  DropdownItem,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton
} from "@ellucian/react-design-system/core";
import ConfirmationDialog from "./ConfirmationDialog";
import { appConfig } from "../../utils/config";
import { trackSolutionInfoRequest } from "../../utils/analytics";
import "./styles/RequestInfoDialog.css";

interface RequestInfoForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country: string;
  institution: string;
  jobLevel: string;
  functionalRole: string;
}

interface RequestInfoDialogProps {
  open: boolean;
  onClose: () => void;
  form: RequestInfoForm;
  setForm: React.Dispatch<React.SetStateAction<RequestInfoForm>>;
  formValid: boolean;
  productName: string;
  solution?: any; // Add solution prop for analytics
  onSubmitOverride?: (form: RequestInfoForm) => Promise<void>; // When provided, replaces the default /api/leads POST
}

const RequestInfoDialog: React.FC<RequestInfoDialogProps> = ({
  open,
  onClose,
  form,
  setForm,
  formValid,
  productName,
  solution,
  onSubmitOverride
}) => {
  const [jobLevelOpen, setJobLevelOpen] = useState(false);
  const [functionalRoleOpen, setFunctionalRoleOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Validation state
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [countries, setCountries] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [countriesError, setCountriesError] = useState(false);

  // Search state
  const [searchString, setSearchString] = useState("");
  const [searchResults, setSearchResults] = useState<
    Array<{ id: number; name: string }>
  >([]);

  // Add at the top with other state
  const [filterTimeout, setFilterTimeout] = useState<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Add state for focused index
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [countryFocused, setCountryFocused] = useState(false);

  React.useEffect(() => {
    if (open) {
      setTouched({});
      setSubmitAttempted(false);
      setErrorMessage("");
      setSearchString("");
      setSearchResults([]);

      const fetchCountries = async () => {
        setLoadingCountries(true);
        try {
          const response = await fetch(
            `${appConfig.api.baseUrl}/api/countries`
          );
          const data = await response.json();
          if (data.success) {
            setCountries(data.data);
            setCountriesError(false);
          } else {
            setCountriesError(true);
          }
        } catch (error) {
          setCountriesError(true);
        } finally {
          setLoadingCountries(false);
        }
      };

      fetchCountries();
    }
  }, [open]);

  // Effect to handle pre-filled country data
  React.useEffect(() => {
    if (open && form.country && form.country.trim() !== "" && searchString === "") {
      // Set the search string to match the pre-filled country
      setSearchString(form.country);
    }
  }, [open, form.country, searchString]);

  const onFilterCountries = async (value: string) => {
    if (!value.trim()) {
      setSearchResults([]);
      return;
    }

    setLoadingCountries(true);
    try {
      const searchLower = value.toLowerCase();

      const startsWithMatches = countries.filter((country) =>
        country.name.toLowerCase().startsWith(searchLower)
      );

      const containsMatches = countries.filter(
        (country) =>
          country.name.toLowerCase().includes(searchLower) &&
          !country.name.toLowerCase().startsWith(searchLower)
      );

      const filtered = [...startsWithMatches, ...containsMatches];

      setSearchResults(filtered.slice(0, 10));
    } catch (error) {
      setSearchResults([]);
    } finally {
      setLoadingCountries(false);
    }
  };

  const onCountrySelect = (event: React.MouseEvent, index: number) => {
    const selectedCountry = searchResults[index];
    setSearchString(selectedCountry.name);
    setForm((f) => ({ ...f, country: selectedCountry.name }));
    setSearchResults([]);
  };

  const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchString(value);

    if (filterTimeout) {
      clearTimeout(filterTimeout);
    }

    if (!value.trim()) {
      setForm((f) => ({ ...f, country: "" }));
      setSearchResults([]);
    } else {
      setForm((f) => ({ ...f, country: "" }));

      // Debounce filtering by 300ms
      const timeout = setTimeout(() => {
        onFilterCountries(value);
      }, 300);
      setFilterTimeout(timeout);
    }

    setFocusedIndex(-1); // Reset focus on new search
  };

  const onSearchClear = () => {
    setSearchString("");
    setSearchResults([]);
    setForm((f) => ({ ...f, country: "" }));
  };

  // Validation logic
  const errors: { [key: string]: string } = {};
  if (!form.firstName.trim()) errors.firstName = "First name is required.";
  if (!form.lastName.trim()) errors.lastName = "Last name is required.";
  if (!form.email.trim()) {
    errors.email = "Email is required.";
  } else if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
    errors.email = "Email address is invalid.";
  }
  if (form.phone.trim()) {
    const phonePattern = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;
    const digits = form.phone.replace(/\D/g, "");
    if (!phonePattern.test(form.phone.trim()) || digits.length < 7) {
      errors.phone = "Phone number is invalid.";
    }
  }

  if (!form.country.trim()) {
    errors.country = "Country is required.";
  } else if (countriesError) {
    errors.country = "Unable to load countries. Please refresh the page and try again.";
  } else if (countries.length > 0 && !countries.some((c) => c.name === form.country)) {
    errors.country = "Please select a valid country from the list.";
  }
  if (!form.institution.trim())
    errors.institution = "Institution name is required.";
  if (!form.jobLevel.trim()) errors.jobLevel = "Job level is required.";
  if (!form.functionalRole.trim())
    errors.functionalRole = "Functional role is required.";

  const showError = (field: string) =>
    (submitAttempted || touched[field]) && errors[field];

  const handleDialogClose = (event: object, reason?: string) => {
    if (reason === "backdropClick") return;
    onClose();
  };

  const handleBlur = (field: string) => {
    setTouched((t) => ({ ...t, [field]: true }));
  };

  const handleSubmit = async () => {
    if (Object.keys(errors).length === 0) {
      setSubmitting(true);
      try {
        if (onSubmitOverride) {
          await onSubmitOverride(form);
          if (solution) {
            trackSolutionInfoRequest(solution, 'contact_form', form.institution);
          }
          onClose();
          setConfirmationOpen(true);
          return;
        }
        const payload = {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          companyName: form.institution,
          productName,
          description:
            "Request for more information submitted from Marketplace UI",
          phone: form.phone || "",
          country: form.country,
          Department__cf: form.jobLevel,
          Function__cf: form.functionalRole
        };

        const response = await fetch(`${appConfig.api.baseUrl}/api/leads`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "cookie-based-auth", // Required for API Gateway to invoke authorizer with cookie-only auth
          },
          credentials: "include",
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          throw new Error(
            `Server error: ${response.status} ${response.statusText}`
          );
        }

        const data = await response.json();

        if (!data.success) {
          setErrorMessage(data.message || "Submission failed");
          return;
        }
        // Track successful info request submission (lead generation)
        if (solution) {
          trackSolutionInfoRequest(solution, 'contact_form', form.institution);
        } else {
          console.warn('No solution data available for tracking');
        }
        
        onClose(); 
        setConfirmationOpen(true);
      } catch (error) {
        let message = "An error occurred. Please try again.";

        if (error instanceof TypeError && error.message.includes("fetch")) {
          message = "Network error. Please check your internet connection.";
        } else if (error instanceof Error) {
          message = error.message;
        }

        setErrorMessage(message);
      } finally {
        setSubmitting(false);
      }
    } else {
      setSubmitAttempted(true);
    }
  };

  const handleConfirmationClose = () => {
    setConfirmationOpen(false);
    // Reset form after confirmation dialog closes
    setForm({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      country: "",
      institution: "",
      jobLevel: "",
      functionalRole: ""
    });
  };

  const unifiedFontStack =
    '"Proxima Nova", Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif';
  const unifiedFontStyles = {
    color: "var(--Neutral-neutral-600, #151618)",
    fontFamily: unifiedFontStack,
    fontSize: 16,
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "125%",
    letterSpacing: "0.08px"
  };
  const errorTextStyles = {
    fontFamily: unifiedFontStack,
    fontSize: "0.75rem",
    fontWeight: 400,
    color: "#d32f2f",
    marginLeft: "0px",
    marginTop: "3px",
    textTransform: "none"
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        id="requestInfoDialog_container"
        PaperProps={{
          id: "requestInfoDialog_paper",
          sx: {
            maxWidth: { xs: "calc(100% - 32px)", sm: 640 },
            margin: { xs: "16px", sm: "32px auto" },
            borderRadius: "4px",
            maxHeight: { xs: "calc(100vh - 32px)", sm: "90vh" },
            overflow: "hidden"
          }
        }}
      >
        <DialogTitle sx={{ mb: { xs: 2, sm: 4 }, p: 0 }}>
          <Typography
            variant="h2"
            sx={{
              color: "var(--Neutral-neutral-600, #151618)",
              fontFamily:
                'Poppins, Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
              fontSize: { xs: "20px", sm: "24px" },
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "120%",
              wordBreak: "break-word",
              m: 0,
              p: 0,
              pr: { xs: 4, sm: 0 } // Space for close button on mobile
            }}
          >
            Contact Us to Learn More
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            overflowY: "auto",
            overflowX: "hidden",
            px: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 3 }
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "var(--Neutral-neutral-600, #151618)",
              fontFamily: unifiedFontStack,
              fontSize: { xs: "14px", sm: "16px" },
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "140%",
              letterSpacing: "0.08px",
              mb: { xs: 3, sm: 4 }
            }}
          >
            Please enter your contact information and an Ellucian or Partner
            representative will contact you regarding this solution and provide
            next steps.
          </Typography>
          {errorMessage && (
            <Alert
              alertType="error"
              id="requestInfoDialog_errorAlert"
              open={!!errorMessage}
              onClose={() => setErrorMessage("")}
              text={errorMessage}
              variant="inline"
              sx={{ mb: 4, mt: 2 }}
            />
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 3, sm: 6 },
              mb: { xs: 3, sm: 5 }
            }}
          >
            <TextField
              id="requestInfo_firstName"
              label="First Name*"
              value={form.firstName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setForm((f) => ({ ...f, firstName: e.target.value }))
              }
              onBlur={() => handleBlur("firstName")}
              error={!!showError("firstName")}
              helperText={showError("firstName") ? errors.firstName : ""}
              FormHelperTextProps={{
                sx: errorTextStyles
              }}
              fullWidth
              size="small"
              inputProps={{
                'aria-label': 'First Name',
                style: {
                  ...unifiedFontStyles,
                  paddingTop: 8,
                  paddingBottom: 8,
                  paddingLeft: 14
                }
              }}
              sx={{ borderRadius: 2, ...unifiedFontStyles }}
            />
            <TextField
              id="requestInfo_lastName"
              label="Last Name*"
              value={form.lastName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setForm((f) => ({ ...f, lastName: e.target.value }))
              }
              onBlur={() => handleBlur("lastName")}
              error={!!showError("lastName")}
              helperText={showError("lastName") ? errors.lastName : ""}
              FormHelperTextProps={{
                sx: errorTextStyles
              }}
              fullWidth
              size="small"
              inputProps={{
                'aria-label': 'Last Name',
                style: {
                  ...unifiedFontStyles,
                  paddingTop: 8,
                  paddingBottom: 8,
                  paddingLeft: 14
                }
              }}
              sx={{ borderRadius: 2, ...unifiedFontStyles }}
            />
          </Box>
          <TextField
            id="requestInfo_email"
            label="Email*"
            value={form.email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((f) => ({ ...f, email: e.target.value }))
            }
            onBlur={() => handleBlur("email")}
            error={!!showError("email")}
            helperText={showError("email") ? errors.email : ""}
            FormHelperTextProps={{
              sx: errorTextStyles
            }}
            fullWidth
            sx={{ mb: { xs: 3, sm: 5 }, ...unifiedFontStyles }}
            size="small"
            inputProps={{
              'aria-label': 'Email',
              style: {
                ...unifiedFontStyles,
                paddingTop: 8,
                paddingBottom: 8,
                paddingLeft: 14
              }
            }}
          />
          <TextField
            id="requestInfo_phone"
            label="Phone Number"
            value={form.phone}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((f) => ({ ...f, phone: e.target.value }))
            }
            onBlur={() => handleBlur("phone")}
            error={!!showError("phone")}
            helperText={showError("phone") ? errors.phone : ""}
            FormHelperTextProps={{
              sx: errorTextStyles
            }}
            fullWidth
            sx={{ mb: { xs: 3, sm: 5 }, ...unifiedFontStyles }}
            size="small"
            inputProps={{
              'aria-label': 'Phone Number',
              style: {
                ...unifiedFontStyles,
                paddingTop: 8,
                paddingBottom: 8,
                paddingLeft: 14
              }
            }}
          />
          <Box sx={{ position: "relative", mb: { xs: 3, sm: 5 } }}>
            <TextField
              id="requestInfo_country"
              label="Country*"
              value={searchString}
              InputLabelProps={{ shrink: !!searchString || countryFocused }}
              onChange={onSearchChange}
              onFocus={() => setCountryFocused(true)}
              onBlur={() => { handleBlur("country"); setCountryFocused(false); }}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (searchResults.length === 0) return;

                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  const nextIndex =
                    focusedIndex < searchResults.length - 1
                      ? focusedIndex + 1
                      : 0;
                  setFocusedIndex(nextIndex);
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  const prevIndex =
                    focusedIndex > 0
                      ? focusedIndex - 1
                      : searchResults.length - 1;
                  setFocusedIndex(prevIndex);
                } else if (e.key === "Enter" && focusedIndex >= 0) {
                  e.preventDefault();
                  onCountrySelect(null as any, focusedIndex);
                } else if (e.key === "Escape") {
                  setSearchResults([]);
                  setFocusedIndex(-1);
                }
              }}
              error={!!showError("country") || countriesError}
              helperText={
                countriesError
                  ? "Unable to load countries. Please refresh the page and try again."
                  : showError("country")
                  ? errors.country
                  : ""
              }
              FormHelperTextProps={{
                sx: errorTextStyles
              }}
              fullWidth
              size="small"
              InputProps={{
                endAdornment: searchString && (
                  <IconButton
                    onClick={onSearchClear}
                    size="small"
                    disableRipple
                    disableFocusRipple
                    sx={{
                      mr: 1,
                      padding: 0,
                      color: "#000 !important",
                      backgroundColor: "transparent !important",
                      "&:hover": {
                        backgroundColor: "transparent !important",
                        color: "#000 !important"
                      },
                      "&:focus": {
                        backgroundColor: "transparent !important",
                        color: "#000 !important"
                      },
                      "&:active": {
                        backgroundColor: "transparent !important",
                        color: "#000 !important"
                      }
                    }}
                  >
                    ✕
                  </IconButton>
                )
              }}
              inputProps={{
                style: {
                  ...unifiedFontStyles,
                  paddingTop: 8,
                  paddingBottom: 8,
                  paddingLeft: 14
                },
                autoComplete: "off",
                'aria-label': 'Country'
              }}
              sx={{ borderRadius: 2, ...unifiedFontStyles }}
            />
            {loadingCountries && (
              <Box sx={{ position: "absolute", right: 40, top: 12 }}>
                <CircularProgress size={20} aria-label="Loading" />
              </Box>
            )}
            {/* Only show "No results" if user is typing AND countries are loaded AND no results found */}
            {searchString &&
              !loadingCountries &&
              searchResults.length === 0 &&
              countries.length > 0 &&
              !form.country && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                    zIndex: 1000,
                    mt: 0.5,
                    padding: "12px 16px"
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontFamily: unifiedFontStack,
                      color: "#666",
                      fontStyle: "italic"
                    }}
                  >
                    No countries found. Please try a different search.
                  </Typography>
                </Box>
              )}
            {/* Only show results when actively searching (not after selection) */}
            {searchResults.length > 0 && !form.country && (
              <Box
                sx={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  maxHeight: { xs: 150, sm: 200 }, // Smaller on mobile
                  overflowY: "auto",
                  backgroundColor: "#fff",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  zIndex: 1000,
                  mt: 0.5,
                  borderTop: "none"
                }}
              >
                {searchResults.map((country, index) => (
                  <Box
                    key={country.id}
                    onClick={() => onCountrySelect(null as any, index)}
                    sx={{
                      padding: "12px 16px",
                      cursor: "pointer",
                      backgroundColor:
                        focusedIndex === index ? "#e3f2fd" : "transparent",
                      "&:hover": {
                        backgroundColor:
                          focusedIndex === index ? "#e3f2fd" : "#f5f5f5"
                      },
                      borderBottom:
                        index < searchResults.length - 1
                          ? "1px solid #eee"
                          : "none"
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 14, fontFamily: unifiedFontStack }}
                    >
                      {country.name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <TextField
            id="requestInfo_institution"
            label="Institution Name*"
            value={form.institution}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((f) => ({ ...f, institution: e.target.value }))
            }
            onBlur={() => handleBlur("institution")}
            error={!!showError("institution")}
            helperText={showError("institution") ? errors.institution : ""}
            FormHelperTextProps={{
              sx: errorTextStyles
            }}
            fullWidth
            sx={{ mb: { xs: 3, sm: 5 }, ...unifiedFontStyles }}
            size="small"
            inputProps={{
              'aria-label': 'Institution Name',
              style: {
                ...unifiedFontStyles,
                paddingTop: 8,
                paddingBottom: 8,
                paddingLeft: 14
              }
            }}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 3, sm: 6 },
              mb: 3,
              width: "100%"
            }}
          >
            <Box sx={{ flex: 1, width: "100%" }}>
              <Dropdown
                id="requestInfoDialog_jobLevel"
                label="Job Level*"
                value={form.jobLevel}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                  setForm((f) => ({ ...f, jobLevel: event.target.value }))
                }
                onBlur={() => handleBlur("jobLevel")}
                open={jobLevelOpen}
                onOpen={() => setJobLevelOpen(true)}
                onClose={() => setJobLevelOpen(false)}
                fullWidth
                className={`request-info-dropdown request-info-input force-font dropdown-bold${
                  showError("jobLevel") ? " request-info-error" : ""
                }`}
                size="small"
                inputProps={{ 'aria-label': 'Job Level' }}
                sx={{ ...unifiedFontStyles, fontWeight: 400 }}
                style={{
                  color: "var(--Neutral-neutral-600, #151618)",
                  fontFamily: unifiedFontStack,
                  fontSize: 16,
                  fontWeight: 400,
                  lineHeight: "125%",
                  letterSpacing: "0.08px"
                }}
              >
                <DropdownItem label="Select" value="" />
                <DropdownItem label="Executive" value="Executive" />
                <DropdownItem label="Manager" value="Manager" />
                <DropdownItem label="Staff" value="Staff" />
                <DropdownItem label="Faculty" value="Faculty" />
                <DropdownItem label="Other" value="Other" />
              </Dropdown>
              {showError("jobLevel") && (
                <Typography
                  variant="caption"
                  className="request-info-error-message"
                  sx={{ ...errorTextStyles, fontFamily: unifiedFontStack }}
                >
                  {errors.jobLevel}
                </Typography>
              )}
            </Box>
            <Box sx={{ flex: 1, width: "100%" }}>
              <Dropdown
                id="requestInfoDialog_functionalRole"
                label="Functional Role*"
                value={form.functionalRole}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                  setForm((f) => ({ ...f, functionalRole: event.target.value }))
                }
                onBlur={() => handleBlur("functionalRole")}
                open={functionalRoleOpen}
                onOpen={() => setFunctionalRoleOpen(true)}
                onClose={() => setFunctionalRoleOpen(false)}
                fullWidth
                className={`request-info-dropdown request-info-input force-font dropdown-bold${
                  showError("functionalRole") ? " request-info-error" : ""
                }`}
                size="small"
                inputProps={{ 'aria-label': 'Functional Role' }}
                sx={{ ...unifiedFontStyles, fontWeight: 400 }}
                style={{
                  color: "var(--Neutral-neutral-600, #151618)",
                  fontFamily: unifiedFontStack,
                  fontSize: 16,
                  fontWeight: 400,
                  lineHeight: "125%",
                  letterSpacing: "0.08px"
                }}
              >
                <DropdownItem label="Select" value="" />
                <DropdownItem label="IT" value="IT" />
                <DropdownItem label="Finance" value="Finance" />
                <DropdownItem
                  label="Academic Affairs"
                  value="Academic Affairs"
                />
                <DropdownItem
                  label="Student Services"
                  value="Student Services"
                />
                <DropdownItem label="Other" value="Other" />
              </Dropdown>
              {showError("functionalRole") && (
                <Typography
                  variant="caption"
                  className="request-info-error-message"
                  sx={{ ...errorTextStyles, fontFamily: unifiedFontStack }}
                >
                  {errors.functionalRole}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 3 },
            pt: { xs: 1, sm: 2 },
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: { xs: 1, sm: 2 },
            "& > button": {
              width: { xs: "100%", sm: "auto" }
            }
          }}
        >
          <Button
            onClick={onClose}
            color="secondary"
            sx={{
              color: "var(--Interactive-Blue-cta-blue-600, #026BC8)",
              fontFamily:
                'Poppins, Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "110%",
              textTransform: "capitalize",
              minHeight: { xs: "44px", sm: "auto" } // Better touch target on mobile
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            disabled={submitting}
            sx={{
              color: "var(--Neutral-neutral-100, #FFF)",
              fontFamily:
                'Poppins, Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "110%",
              textTransform: "capitalize",
              minHeight: { xs: "44px", sm: "auto" } // Better touch target on mobile
            }}
          >
            {submitting ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmationOpen}
        onClose={handleConfirmationClose}
        onDone={handleConfirmationClose}
      />
    </>
  );
};

export default RequestInfoDialog;
